import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardCover = ({ orgName, logoUrl, subtitle, accentColor = '#F5831F', onSettingsClick, onRevealed }) => {
  // Show the full splash once per browser session; later dashboard visits
  // start revealed so the cover never re-blocks navigation (same class of
  // bug as the ocean-intro overlay fixed in iter29).
  const [revealed, setRevealed] = useState(() => {
    try { return sessionStorage.getItem('hibff_cover_shown') === 'true'; } catch { return false; }
  });
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const coverRef = useRef(null);
  const dragStartY = useRef(0);
  const onRevealedFiredRef = useRef(false);

  // Entrance animation states
  const [logoVisible, setLogoVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [lineVisible, setLineVisible] = useState(false);

  const displayName = orgName || 'HiBFF';
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const fullLogoUrl = logoUrl?.startsWith('http') ? logoUrl : logoUrl ? `${API_URL}${logoUrl}` : null;

  // For HiBFF-branded cover: skip typing the name (logo already says it)
  const isHiBFF = displayName === 'HiBFF';

  // Orchestrate entrance sequence — all timings reduced 15%
  useEffect(() => {
    const t1 = setTimeout(() => setLogoVisible(true), 255);

    if (isHiBFF) {
      // HiBFF: skip typing, just show subtitle + CTA after logo settles
      setTypedText('');
      setTypingDone(true);
      const t3 = setTimeout(() => setSubtitleVisible(true), 850);
      const t4 = setTimeout(() => setCtaVisible(true), 1100);
      const t5 = setTimeout(() => setLineVisible(true), 1270);
      return () => { clearTimeout(t1); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
    }

    // Non-HiBFF: typewriter effect
    const t2 = setTimeout(() => {
      let i = 0;
      const chars = displayName.split('');
      const interval = setInterval(() => {
        i++;
        setTypedText(chars.slice(0, i).join(''));
        if (i >= chars.length) {
          clearInterval(interval);
          setTypingDone(true);
        }
      }, 47);
      return () => clearInterval(interval);
    }, 765);

    const typeDuration = 765 + displayName.length * 47 + 170;
    const t3 = setTimeout(() => setSubtitleVisible(true), typeDuration);
    const t4 = setTimeout(() => setCtaVisible(true), typeDuration + 255);
    const t5 = setTimeout(() => setLineVisible(true), typeDuration + 425);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, [displayName, isHiBFF]);

  // If the cover starts revealed (already shown this session), notify the
  // parent immediately so post-cover onboarding still fires.
  useEffect(() => {
    if (revealed && !onRevealedFiredRef.current && typeof onRevealed === 'function') {
      onRevealedFiredRef.current = true;
      onRevealed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReveal = () => {
    setRevealed(true);
    setDragOffset(0);
    try { sessionStorage.setItem('hibff_cover_shown', 'true'); } catch { /* noop */ }
    // Cover takes ~2.2s (slow, progressive roll) to fully roll up — wait for
    // the slide-up to finish before notifying the parent so any post-cover
    // tooltips/onboarding don't fire on top of the still-animating cover.
    if (!onRevealedFiredRef.current && typeof onRevealed === 'function') {
      onRevealedFiredRef.current = true;
      setTimeout(() => onRevealed(), 2300);
    }
  };

  const handlePullDown = () => {
    setRevealed(false);
    setDragOffset(0);
  };

  const onDragStart = (e) => {
    setDragging(true);
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
  };

  const onDragMove = useCallback((e) => {
    if (!dragging) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = clientY - dragStartY.current;
    if (delta > 0) setDragOffset(Math.min(delta, window.innerHeight));
  }, [dragging]);

  const onDragEnd = useCallback(() => {
    setDragging(false);
    if (dragOffset > 80) {
      setRevealed(false);
    }
    setDragOffset(0);
  }, [dragOffset]);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: true });
    window.addEventListener('touchend', onDragEnd);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, [dragging, onDragMove, onDragEnd]);

  return (
    <>
      {/* Full-screen cover */}
      <div
        ref={coverRef}
        className="fixed inset-0 z-50 flex flex-col items-center justify-center"
        style={{
          backgroundColor: '#FDFBF7',
          transform: revealed ? `translateY(calc(-100% + ${dragOffset}px))` : 'translateY(0)',
          transition: dragging ? 'none' : 'transform 2.2s cubic-bezier(0.45, 0.05, 0.25, 1)',
          willChange: 'transform',
        }}
        data-testid="dashboard-cover"
      >
        {/* Background radials */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, ${accentColor}08 0%, transparent 50%), radial-gradient(circle at 80% 70%, ${accentColor}06 0%, transparent 50%)`,
        }} />

        <div className="relative flex flex-col items-center text-center px-6 max-w-md">

          {/* ── Logo / Initials — fade + scale ── */}
          <div
            style={{
              opacity: logoVisible ? 1 : 0,
              transform: logoVisible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(12px)',
              transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            {fullLogoUrl ? (
              <div className="mb-6" data-testid="cover-logo">
                <img
                  src={fullLogoUrl}
                  alt={displayName}
                  className="object-contain"
                  style={{ maxHeight: isHiBFF ? '180px' : '112px', maxWidth: '320px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="mb-6" data-testid="cover-initials">
                <div
                  className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-lg"
                  style={{ backgroundColor: accentColor }}
                >
                  {initials}
                </div>
              </div>
            )}
          </div>

          {/* ── Org name — typewriter (skipped for HiBFF since logo has the name) ── */}
          {!isHiBFF && (
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3"
              style={{ fontFamily: 'Georgia, serif', color: '#1A1209', minHeight: '1.3em' }}
              data-testid="cover-org-name"
            >
              {typedText}
              {!typingDone && (
                <span
                  className="inline-block w-[3px] ml-0.5 align-middle"
                  style={{
                    height: '0.85em',
                    backgroundColor: accentColor,
                    animation: 'blink-cursor 0.7s step-end infinite',
                  }}
                />
              )}
            </h1>
          )}

          {/* ── Subtitle — fade in (Welcome back, …) ── */}
          {subtitle && (
            <p
              className="text-2xl sm:text-3xl mb-3"
              style={{
                fontFamily: 'Georgia, serif',
                color: '#888',
                opacity: subtitleVisible ? 1 : 0,
                transform: subtitleVisible ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
              }}
              data-testid="cover-subtitle"
            >
              {subtitle}
            </p>
          )}

          {/* Powered by HiBFF */}
          {orgName && !isHiBFF && (
            <p
              className="text-xs mb-8"
              style={{
                color: '#BBB',
                opacity: subtitleVisible ? 1 : 0,
                transition: 'opacity 0.4s ease 0.12s',
              }}
            >
              Powered by HiBFF
            </p>
          )}

          {/* Spacer for HiBFF cover */}
          {isHiBFF && (
            <>
              <p
                className="text-xl sm:text-2xl font-semibold mb-2"
                style={{
                  color: '#1A1209',
                  fontFamily: 'Georgia, serif',
                  opacity: subtitleVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease 0.1s',
                }}
                data-testid="splash-brand-line"
              >
                Hi Brave Friends Forever
              </p>
              <p
                className="text-base italic mb-6 max-w-xl mx-auto px-4"
                style={{
                  color: '#7B4FD8',
                  fontFamily: 'Georgia, serif',
                  opacity: subtitleVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease 0.2s',
                }}
                data-testid="splash-brand-tagline"
              >
                Brave takes courage. Courage builds confidence. Confidence builds character.
              </p>
            </>
          )}

          {/* Logo upload prompt */}
          {!logoUrl && orgName && !isHiBFF && onSettingsClick && (
            <button
              onClick={onSettingsClick}
              className="flex items-center gap-2 text-xs mb-6 px-4 py-2 rounded-full border transition-colors hover:bg-gray-50"
              style={{
                color: '#999',
                borderColor: '#E5E5E5',
                opacity: ctaVisible ? 1 : 0,
                transition: 'opacity 0.35s ease',
              }}
              data-testid="cover-upload-logo-prompt"
            >
              <Upload size={14} />
              Upload your logo to personalize
            </button>
          )}

          {/* ── Enter button — rise in ── */}
          <button
            onClick={handleReveal}
            className="group flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all"
            style={{
              backgroundColor: accentColor,
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.4s cubic-bezier(0.16,1,0.3,1), transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.2s ease',
            }}
            data-testid="cover-enter-btn"
          >
            Enter Dashboard
            <ChevronUp size={18} className="group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>

        {/* ── Bottom line — sweep in ── */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{
            backgroundColor: accentColor,
            opacity: 0.3,
            transform: lineVisible ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.68s cubic-bezier(0.16,1,0.3,1)',
          }}
        />
      </div>

      {/* Pull-down tab — wrapper must NEVER eat taps (it spans the full
          header width invisibly); only the pill itself is interactive. */}
      {revealed && (
        <div
          className="fixed top-0 left-0 right-0 z-50 flex justify-start sm:justify-center pointer-events-none"
          style={{
            transform: `translateY(${dragOffset}px)`,
            transition: dragging ? 'none' : 'transform 0.5s ease',
          }}
        >
          <button
            onMouseDown={onDragStart}
            onTouchStart={onDragStart}
            onClick={handlePullDown}
            className="pointer-events-auto flex items-center gap-0.5 px-2 py-1 ml-12 sm:ml-0 sm:gap-1.5 sm:px-4 sm:py-1.5 rounded-b-lg text-[10px] sm:text-xs font-medium text-white shadow-md cursor-grab active:cursor-grabbing"
            style={{ backgroundColor: accentColor }}
            data-testid="cover-pulldown-tab"
            title="Pull down to show cover"
          >
            <ChevronDown size={12} />
            {displayName}
          </button>
        </div>
      )}

      {/* Cursor blink keyframes */}
      <style>{`
        @keyframes blink-cursor {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </>
  );
};

export default DashboardCover;
